/**
 * Settings → SSO — Enterprise IdP integration (ADR-022 Phase 1 +
 * ADR-023 Phase 2.B).
 *
 * Per-tenant self-service surface (D3). Plan-gated by
 * ``org.sso_enabled`` (D6 write-side fires from the backend with
 * a 402 when the plan doesn't include SSO; this page also
 * renders an inline upgrade banner so the admin sees it BEFORE
 * trying to save).
 *
 * Two protocols supported:
 *   - OIDC (Google Workspace, Microsoft Entra ID, Okta, OneLogin,
 *     Auth0, Auth0). Discovery URL → metadata.
 *   - SAML 2.0 (Okta, Entra ID, Google Workspace via SAML app,
 *     OneLogin, AD FS 2016+, Auth0). Static metadata: entity ID
 *     + SSO URL + IdP signing certificate.
 *
 * SAML metadata XML upload (Phase 2.B):
 *   IdPs export an XML metadata file. Pasting it auto-fills the
 *   entity ID, SSO URL, and certificate fields via client-side
 *   DOMParser extraction. No upload to the server — the parsed
 *   fields go through the same Save flow.
 *
 * Save & Test (D3):
 *   - OIDC: Save persists, then calls /test which fetches the
 *     discovery URL and confirms the metadata document has the
 *     expected endpoint keys.
 *   - SAML: Save persists, then /test validates static fields
 *     (entity_id non-empty, sso_url HTTPS, cert PEM-shaped). No
 *     network call — SAML is self-contained metadata.
 *
 * Disable / Remove (UI distinction):
 *   - "Disable" toggle = is_active=False; row stays.
 *   - "Remove SSO config" button (red, with ConfirmDialog) =
 *     DELETE; row gone, requires re-paste to re-enable.
 *
 * §13 chrome verbatim: px=42 py=12, 1px border + divider, no
 * shadows, buttons borderRadius:1 textTransform:none size:small.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircleOutlined,
  Close as CloseIcon,
  ErrorOutlineOutlined,
  Save,
  ScienceOutlined,
} from '@mui/icons-material';
import RocketLaunchOutlined from '@mui/icons-material/RocketLaunchOutlined';

import { ApiError, systemApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';
import { profileApi } from '../../../api/organization';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import SsoScimSection from './SsoScimSection';
import { activeLocale } from '../../../utils/datetime';
import type {
  SsoConfigResponse,
  SsoConfigTestResponse,
  SsoConfigUpsert,
} from '../../../api/system';

const DEFAULT_FORM: SsoConfigUpsert = {
  protocol: 'oidc',
  email_domain: '',
  is_active: true,
  display_name: '',
  enable_jit_provisioning: false,
  jit_email_domain_whitelist: null,
  oidc_discovery_url: '',
  oidc_client_id: '',
  oidc_client_secret: '',
  oidc_scopes: 'openid email profile',
  oidc_email_claim: 'email',
  saml_entity_id: '',
  saml_sso_url: '',
  saml_certificate: '',
  saml_email_attribute: 'NameID',
};

/**
 * Parse pasted SAML 2.0 IdP metadata XML and extract the three
 * canonical fields. Pure client-side DOMParser; no server round-
 * trip. Returns null fields when the XML is malformed or doesn't
 * match the expected shape — caller should prompt the admin to
 * paste raw fields manually.
 *
 * Extraction rules:
 *   - entity_id: <md:EntityDescriptor entityID="..."> attribute.
 *   - sso_url:   <md:SingleSignOnService Binding="...HTTP-POST"
 *                Location="..."/> Location attribute. Falls back
 *                to HTTP-Redirect binding if POST not present.
 *   - cert:      first <ds:X509Certificate> base64 body, wrapped
 *                in PEM headers. Note that IdP metadata typically
 *                contains BOTH a signing AND encryption cert; we
 *                pick the first under <md:KeyDescriptor use="signing">
 *                or the first overall.
 */
function parseSamlMetadata(xml: string): {
  entity_id: string | null;
  sso_url: string | null;
  certificate: string | null;
  errorCode: 'parseError' | 'extractError' | 'genericError' | null;
  errorDetail: string | null;
} {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      return {
        entity_id: null,
        sso_url: null,
        certificate: null,
        errorCode: 'parseError',
        errorDetail: null,
      };
    }
    // Try multiple namespace-prefixed lookups since IdPs use
    // different prefixes.
    const ed = doc.querySelector(
      'EntityDescriptor, [*|EntityDescriptor]',
    );
    const entity_id = ed?.getAttribute('entityID') ?? null;

    // Prefer HTTP-POST SSO URL, fall back to HTTP-Redirect.
    const ssoServices = Array.from(
      doc.getElementsByTagNameNS('*', 'SingleSignOnService'),
    );
    let sso_url: string | null = null;
    const post = ssoServices.find(
      (n) => n.getAttribute('Binding') === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST',
    );
    const redirect = ssoServices.find(
      (n) => n.getAttribute('Binding') === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect',
    );
    sso_url = post?.getAttribute('Location') ?? redirect?.getAttribute('Location') ?? null;

    // Pick the signing cert if explicitly marked; otherwise the first.
    const keyDescriptors = Array.from(
      doc.getElementsByTagNameNS('*', 'KeyDescriptor'),
    );
    const signingDescriptor = keyDescriptors.find(
      (kd) => kd.getAttribute('use') === 'signing',
    );
    const certElement = (signingDescriptor ?? keyDescriptors[0])
      ?.getElementsByTagNameNS('*', 'X509Certificate')[0];
    const certB64 = certElement?.textContent?.replace(/\s+/g, '') ?? null;
    const certificate = certB64
      ? `-----BEGIN CERTIFICATE-----\n${
          (certB64.match(/.{1,64}/g) ?? [certB64]).join('\n')
        }\n-----END CERTIFICATE-----`
      : null;

    if (!entity_id && !sso_url && !certificate) {
      return {
        entity_id: null,
        sso_url: null,
        certificate: null,
        errorCode: 'extractError',
        errorDetail: null,
      };
    }
    return { entity_id, sso_url, certificate, errorCode: null, errorDetail: null };
  } catch (e) {
    return {
      entity_id: null,
      sso_url: null,
      certificate: null,
      errorCode: 'genericError',
      errorDetail: e instanceof Error ? e.message : String(e),
    };
  }
}

const SSO: React.FC = () => {
  const { t } = useTranslation();
  const [planEnabled, setPlanEnabled] = useState<boolean | null>(null);
  const [config, setConfig] = useState<SsoConfigResponse | null>(null);
  const [form, setForm] = useState<SsoConfigUpsert>(DEFAULT_FORM);
  const [domainInput, setDomainInput] = useState('');
  const [metadataXml, setMetadataXml] = useState('');
  const [metadataMessage, setMetadataMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SsoConfigTestResponse | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profile, current] = await Promise.all([
        profileApi.get().catch(() => null),
        systemApi.systemSsoApi.get().catch((e) => {
          // Authenticated GET should succeed; 403 means the role
          // lacks system.sso.read.
          if (e instanceof ApiError && e.status === 403) {
            return null;
          }
          throw e;
        }),
      ]);
      setPlanEnabled(profile ? Boolean(profile.sso_enabled) : null);
      setConfig(current);
      if (current) {
        setForm({
          protocol: current.protocol,
          email_domain: current.email_domain ?? '',
          is_active: current.is_active,
          display_name: current.display_name ?? '',
          enable_jit_provisioning: current.enable_jit_provisioning,
          jit_email_domain_whitelist:
            current.jit_email_domain_whitelist ?? null,
          oidc_discovery_url: current.oidc_discovery_url ?? '',
          oidc_client_id: current.oidc_client_id ?? '',
          oidc_client_secret: '', // never returned; admin re-pastes to rotate
          oidc_scopes: current.oidc_scopes,
          oidc_email_claim: current.oidc_email_claim,
          saml_entity_id: current.saml_entity_id ?? '',
          saml_sso_url: current.saml_sso_url ?? '',
          saml_certificate: current.saml_certificate ?? '',
          saml_email_attribute: current.saml_email_attribute || 'NameID',
        });
      } else {
        setForm(DEFAULT_FORM);
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToLoad'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ---- form helpers ----

  const set = <K extends keyof SsoConfigUpsert>(
    key: K,
    value: SsoConfigUpsert[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const addDomain = () => {
    const v = domainInput.trim().toLowerCase();
    if (!v) return;
    if (v.includes('@') || v.includes('/') || v.includes(' ') || !v.includes('.')) {
      setError(t('system:sso.domainInvalidError', { domain: v }));
      return;
    }
    setError(null);
    const existing = form.jit_email_domain_whitelist ?? [];
    if (existing.includes(v)) {
      setDomainInput('');
      return;
    }
    set('jit_email_domain_whitelist', [...existing, v]);
    setDomainInput('');
  };

  const removeDomain = (d: string) => {
    const existing = form.jit_email_domain_whitelist ?? [];
    const next = existing.filter((x) => x !== d);
    set('jit_email_domain_whitelist', next.length > 0 ? next : null);
  };

  const handleParseMetadata = () => {
    const xml = metadataXml.trim();
    if (!xml) {
      setMetadataMessage({
        severity: 'error',
        text: t('system:sso.metadataPasteFirst'),
      });
      return;
    }
    const parsed = parseSamlMetadata(xml);
    if (parsed.errorCode) {
      const text =
        parsed.errorCode === 'parseError'
          ? t('system:sso.metadataParseError')
          : parsed.errorCode === 'extractError'
          ? t('system:sso.metadataExtractError')
          : `${t('system:sso.metadataParseError')} ${parsed.errorDetail ?? ''}`.trim();
      setMetadataMessage({ severity: 'error', text });
      return;
    }
    setForm((f) => ({
      ...f,
      saml_entity_id: parsed.entity_id ?? f.saml_entity_id,
      saml_sso_url: parsed.sso_url ?? f.saml_sso_url,
      saml_certificate: parsed.certificate ?? f.saml_certificate,
    }));
    const filled: string[] = [];
    if (parsed.entity_id) filled.push(t('system:sso.metadataFieldEntityId'));
    if (parsed.sso_url) filled.push(t('system:sso.metadataFieldSsoUrl'));
    if (parsed.certificate) filled.push(t('system:sso.metadataFieldCertificate'));
    setMetadataMessage({
      severity: 'success',
      text: t('system:sso.metadataAutoFilled', { fields: filled.join(', ') }),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setTestResult(null);
    try {
      // Don't send empty-string client_secret on update — preserve
      // existing encrypted value. Only send when admin pasted
      // something new.
      const payload: SsoConfigUpsert = {
        ...form,
        oidc_client_secret: form.oidc_client_secret || null,
      };
      const saved = await systemApi.systemSsoApi.upsert(payload);
      setConfig(saved);
      setForm((f) => ({ ...f, oidc_client_secret: '' }));
      setToast(t('system:sso.toastCreated', { name: 'SSO' }));
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('system:sso.saveFailed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndTest = async () => {
    await handleSave();
    if (error) return;
    setTesting(true);
    try {
      const result = await systemApi.systemSsoApi.test();
      setTestResult(result);
      setToast(
        result.ok
          ? form.protocol === 'saml'
            ? t('system:sso.test.samlValidatedToast')
            : t('system:sso.test.oidcValidatedToast')
          : t('system:sso.test.problemToast'),
      );
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:sso.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError(null);
    try {
      await systemApi.systemSsoApi.remove();
      setConfig(null);
      setForm(DEFAULT_FORM);
      setTestResult(null);
      setMetadataXml('');
      setMetadataMessage(null);
      setToast(t('system:sso.toastDeleted'));
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:sso.failedToDelete'));
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  // ---- Save-button enablement ----
  const oidcReady =
    !!form.email_domain &&
    !!form.oidc_discovery_url &&
    !!form.oidc_client_id &&
    (Boolean(config?.oidc_client_secret_set) || !!form.oidc_client_secret);
  const samlReady =
    !!form.email_domain &&
    !!form.saml_entity_id &&
    !!form.saml_sso_url &&
    (form.saml_certificate?.includes('BEGIN CERTIFICATE') ?? false);
  const saveAndTestReady = form.protocol === 'oidc' ? oidcReady : samlReady;

  // ---- render ----

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      {/* HANDOFF_2026-05-25 Bug 21 — drop the ShieldOutlined icon prefix.
          No other System / Settings page renders an icon next to the
          h6 title (Notifications, Branding, Roles, etc. all use plain
          h6 + body2 subtitle). Owner caught the inconsistency. */}
      {/* Title, connection state, and the one DESTRUCTIVE action on a single row. The primary
          actions are NOT here: they are in the sticky bar at the bottom, matching
          Attendance → Rules (GlobalRules.tsx:535). The owner asked whether Save could go top-right
          and also asked to unify with that page — the two point different ways, and sticky wins on
          both counts. It is visible at every scroll position, which is the actual complaint (Save
          used to sit ~1100px down behind a divider), whereas a header action scrolls away as soon
          as the admin starts filling the form. Top-right is for page-level verbs like Add or
          Refresh; a form's primary action belongs at the end of the form, kept in reach. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ width: '100%', mb: 0.5 }}
      >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={600} fontSize="1.125rem">
          {t('system:sso.title')}
        </Typography>
        {/* State belongs in the header, not 60px into the form. Inside the card this occupied a
            full row whose right two-thirds were empty. */}
        {config ? (
          <>
            <Chip
              size="small"
              label={config.is_active ? t('system:sso.statusActive') : t('system:sso.statusDisabled')}
              color={config.is_active ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
            />
            <Chip
              size="small"
              label={config.protocol === 'saml' ? t('system:sso.form.protocolSaml') : 'OIDC'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
            />
          </>
        ) : (
          <Chip
            size="small"
            label={t('system:sso.statusNotConfigured')}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
          />
        )}
      </Stack>
        {!loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {config ? (
              <Button
                size="small"
                color="error"
                onClick={() => setConfirmRemove(true)}
                disabled={removing}
                sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
              >
                {t('system:sso.removeConfiguration')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('system:sso.subtitle')}
      </Typography>

      {/* Plan-gate banner — shown only when sso_enabled=False on
          the org. Defense-in-depth: backend also rejects PUT with
          a 402 in that case. */}
      {planEnabled === false ? (
        <Alert
          severity="error"
          sx={{ mb: 2, width: '100%' }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RocketLaunchOutlined fontSize="small" />}
              onClick={() => window.dispatchEvent(new CustomEvent('svdy:upgrade:open'))}
              sx={{ borderRadius: 1, textTransform: 'none' }}
            >
              {t('system:sso.upgradePlan')}
            </Button>
          }
        >
          {t('system:sso.planGateMessage')}
        </Alert>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, width: '100%' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      ) : null}

      {testResult ? (
        <Alert
          severity={testResult.ok ? 'success' : 'warning'}
          icon={
            testResult.ok ? <CheckCircleOutlined /> : <ErrorOutlineOutlined />
          }
          sx={{ mb: 2, width: '100%' }}
          onClose={() => setTestResult(null)}
        >
          <Typography variant="body2" fontWeight={600}>
            {testResult.ok ? (form.protocol === 'saml' ? t('system:sso.test.samlValidated') : t('system:sso.test.oidcValidated')) : t('system:sso.test.problem')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {testResult.detail}
          </Typography>
          {testResult.ok && testResult.discovered_authorization_endpoint ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
              {t('system:sso.test.authEndpointPrefix')} {testResult.discovered_authorization_endpoint}
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {/* SAML cert expiry banner (Phase 2.B #4): show whenever the
          server-parsed notAfter is within 30 days. Stays visible
          until admin rotates the cert (re-paste); not dismissable
          because it's a real action item. */}
      {(() => {
        if (!config || config.protocol !== 'saml' || !config.saml_cert_not_after) return null;
        const expiresAt = new Date(config.saml_cert_not_after);
        const msPerDay = 86400 * 1000;
        const daysLeft = Math.floor((expiresAt.getTime() - Date.now()) / msPerDay);
        if (daysLeft > 30) return null;
        const isExpired = daysLeft < 0;
        const absDays = Math.abs(daysLeft);
        return (
          <Alert
            severity={isExpired ? 'error' : 'warning'}
            icon={<ErrorOutlineOutlined />}
            sx={{ mb: 2, width: '100%' }}
          >
            <Typography variant="body2" fontWeight={600}>
              {isExpired
                ? t('system:sso.certExpiry.expiredDays', { count: absDays })
                : t('system:sso.certExpiry.expiresInDays', { count: daysLeft })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {isExpired
                ? t('system:sso.certExpiry.expiredAction')
                : t('system:sso.certExpiry.expiresAction')}{' '}
              <strong>{t('system:sso.certExpiry.expiresLabel')}</strong> {expiresAt.toLocaleDateString(activeLocale(), { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Alert>
        );
      })()}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            width: '100%',
            // px 2.5 / py 2, matching the config cards on Attendance → Rules. p:3 put 24px on
            // every side of a form that is already generously spaced internally.
            px: 2.5,
            py: 2,
          }}
        >
          {/* Last sign-in is the only per-connection fact worth carrying inside the card; the
              status chips and every action now live in the page header. An empty container was
              left here by the previous pass and opened the card with ~64px of nothing. */}
          {config?.last_login_at ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('system:sso.lastLoginPrefix')} {new Date(config.last_login_at).toLocaleString(activeLocale())}
            </Typography>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('system:sso.sectionGeneral')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="sso-protocol-label">{t('system:sso.form.protocolLabel')}</InputLabel>
                <Select
                  labelId="sso-protocol-label"
                  label={t('system:sso.form.protocolLabel')}
                  value={form.protocol}
                  onChange={(e) =>
                    set('protocol', e.target.value as 'oidc' | 'saml')
                  }
                >
                  <MenuItem value="oidc">{t('system:sso.form.protocolOidc')}</MenuItem>
                  <MenuItem value="saml">{t('system:sso.form.protocolSaml')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('system:sso.form.emailDomainLabel')}
                size="small"
                required
                placeholder="acme.com"
                value={form.email_domain}
                onChange={(e) => set('email_domain', e.target.value)}
                helperText={t('system:sso.form.emailDomainHelper')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('system:sso.form.displayNameLabel')}
                size="small"
                placeholder="Acme SSO"
                value={form.display_name ?? ''}
                onChange={(e) => set('display_name', e.target.value)}
                helperText={t('system:sso.form.displayNameHelper')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => set('is_active', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('system:sso.form.activeLabel')}</Typography>}
              />
            </Grid>

            {form.protocol === 'oidc' ? (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('system:sso.sectionOidc')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.oidcDiscoveryUrlLabel')}
                    size="small"
                    required
                    placeholder="https://accounts.google.com/.well-known/openid-configuration"
                    value={form.oidc_discovery_url ?? ''}
                    onChange={(e) => set('oidc_discovery_url', e.target.value)}
                    helperText={t('system:sso.form.oidcDiscoveryUrlHelper')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.oidcClientIdLabel')}
                    size="small"
                    required
                    value={form.oidc_client_id ?? ''}
                    onChange={(e) => set('oidc_client_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      config?.oidc_client_secret_set
                        ? t('system:sso.form.oidcClientSecretRotateLabel')
                        : t('system:sso.form.oidcClientSecretLabel')
                    }
                    size="small"
                    required={!config?.oidc_client_secret_set}
                    type="password"
                    placeholder={config?.oidc_client_secret_set ? t('system:sso.form.oidcClientSecretOnFile') : ''}
                    value={form.oidc_client_secret ?? ''}
                    onChange={(e) => set('oidc_client_secret', e.target.value)}
                    helperText={t('system:sso.form.oidcClientSecretHelper')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.oidcScopesLabel')}
                    size="small"
                    value={form.oidc_scopes}
                    onChange={(e) => set('oidc_scopes', e.target.value)}
                    helperText={t('system:sso.form.oidcScopesHelper')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.oidcEmailClaimLabel')}
                    size="small"
                    value={form.oidc_email_claim}
                    onChange={(e) => set('oidc_email_claim', e.target.value)}
                    helperText={t('system:sso.form.oidcEmailClaimHelper')}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('system:sso.sectionSaml')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('system:sso.form.samlIntro')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={8}
                    size="small"
                    label={t('system:sso.form.samlMetadataLabel')}
                    placeholder="<?xml version=&quot;1.0&quot;?>&#10;<EntityDescriptor xmlns=&quot;urn:oasis:names:tc:SAML:2.0:metadata&quot; entityID=&quot;https://sso.acme.com/saml&quot;>&#10;  ..."
                    value={metadataXml}
                    onChange={(e) => setMetadataXml(e.target.value)}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleParseMetadata}
                      disabled={!metadataXml.trim()}
                      sx={{ borderRadius: 1, textTransform: 'none' }}
                    >
                      {t('system:sso.form.samlAutoFillButton')}
                    </Button>
                    {metadataMessage ? (
                      <Typography
                        variant="caption"
                        color={
                          metadataMessage.severity === 'success'
                            ? 'success.main'
                            : 'error.main'
                        }
                      >
                        {metadataMessage.text}
                      </Typography>
                    ) : null}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.samlEntityIdLabel')}
                    size="small"
                    required
                    placeholder="https://sso.acme.com/saml/idp"
                    value={form.saml_entity_id ?? ''}
                    onChange={(e) => set('saml_entity_id', e.target.value)}
                    helperText={t('system:sso.form.samlEntityIdHelper')}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.samlSsoUrlLabel')}
                    size="small"
                    required
                    placeholder="https://sso.acme.com/saml/sso"
                    value={form.saml_sso_url ?? ''}
                    onChange={(e) => set('saml_sso_url', e.target.value)}
                    helperText={t('system:sso.form.samlSsoUrlHelper')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('system:sso.form.samlEmailAttributeLabel')}
                    size="small"
                    value={form.saml_email_attribute}
                    onChange={(e) => set('saml_email_attribute', e.target.value)}
                    helperText={t('system:sso.form.samlEmailAttributeHelper')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={12}
                    size="small"
                    label={t('system:sso.form.samlCertificateLabel')}
                    required
                    placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDpDCCAoygAwIBAgIGAYj...&#10;-----END CERTIFICATE-----"
                    value={form.saml_certificate ?? ''}
                    onChange={(e) => set('saml_certificate', e.target.value)}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                    helperText={t('system:sso.form.samlCertificateHelper')}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                {t('system:sso.sectionJit')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }} dangerouslySetInnerHTML={{ __html: t('system:sso.form.jitDescription') }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.enable_jit_provisioning}
                    onChange={(e) => set('enable_jit_provisioning', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('system:sso.form.jitToggleLabel')}</Typography>}
              />
            </Grid>
            {form.enable_jit_provisioning ? (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label={t('system:sso.form.jitDomainLabel')}
                    size="small"
                    placeholder="acme.com"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addDomain();
                      }
                    }}
                    sx={{ minWidth: 240 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={addDomain}
                            disabled={!domainInput.trim()}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ flex: 1, mt: 0.5 }}>
                    {(form.jit_email_domain_whitelist ?? []).map((d) => (
                      <Chip
                        key={d}
                        label={d}
                        size="small"
                        onDelete={() => removeDomain(d)}
                        deleteIcon={<CloseIcon fontSize="small" />}
                        sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Grid>
            ) : null}
          </Grid>

          {/* One component owns this bar for the whole app — see StickyFormActions for the
              research behind primary-first / left-aligned. `bleed 2.5` and `flushBottom 2` cancel
              this CARD's padding rather than the page gutter, because the page also carries the
              SCIM card and a page-wide bar would read as belonging to both. */}
          <StickyFormActions
            bleed={2.5}
            flushBottom={2}
            primary={(
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} /> : <Save />}
                onClick={handleSave}
                disabled={saving || testing || planEnabled === false || !form.email_domain}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {saving ? t('system:sso.saving') : t('system:sso.save')}
              </Button>
            )}
            extra={(
              <Button
                size="small"
                variant="outlined"
                startIcon={testing ? <CircularProgress size={14} /> : <ScienceOutlined />}
                onClick={handleSaveAndTest}
                disabled={saving || testing || planEnabled === false || !saveAndTestReady}
                sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
              >
                {testing ? t('system:sso.testing') : t('system:sso.saveAndTest')}
              </Button>
            )}
          />
        </Box>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={t('system:sso.removeConfirmTitle')}
        description={t('system:sso.removeConfirmDescription')}
        confirmLabel={t('system:sso.removeConfirmButton')}
        tone="danger"
        onConfirm={handleRemove}
        onClose={() => setConfirmRemove(false)}
      />

      {/* SCIM 2.0 provisioning admin block (ADR-024 Phase 3.A
          token CRUD + Phase 3.B group mappings). Plan-gated by
          the same sso_enabled flag — if SSO isn't licensed, hide
          the SCIM block too rather than show endpoints that
          will 402. */}
      {/* Also gated on `config`: SCIM pushes staff from the identity provider, so with no provider
          connected there is nothing to push from. Before this, an org whose status read "Not
          configured" was still shown a Generate-token button and an empty group-mapping table —
          machinery for a connection that does not exist, and the first thing on screen after a
          form the admin has not finished. */}
      {planEnabled !== false && config ? <SsoScimSection /> : null}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={toast ?? ''}
      />
    </Box>
  );
};

export default SSO;
